import { PropsWithChildren, useEffect, useState } from "react";
import type { User } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import AuthContext from "../contexts/AuthContext";
import Splash from "../assets/splash.gif";
import { useTranslation } from "react-i18next";

const ROUTES_WITHOUT_SPLASH = ["/l/"];

function isNoSplashRoute(): boolean {
  return ROUTES_WITHOUT_SPLASH.some((route) =>
    window.location.pathname.startsWith(route),
  );
}

export default function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(!isNoSplashRoute());
  const [fadeOut, setFadeOut] = useState(false);
  const [hasSeenSplash] = useState(sessionStorage.getItem("hasSeenSplash") === "true");
  const [gifKey, setGifKey] = useState(Date.now());

  const { t } = useTranslation();

  useEffect(() => {
    let animationTimer: NodeJS.Timeout;

    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);

      // Reset splash flag on logout
      if (!user) {
        sessionStorage.removeItem("hasSeenSplash");
      }

      if (!hasSeenSplash) {
        // First load in this session: show animation
        sessionStorage.setItem("hasSeenSplash", "true");
        setGifKey(Date.now());
        animationTimer = setTimeout(() => {
          setFadeOut(true);
          setTimeout(() => setLoading(false), 500);
        }, 2000);
      } else {
        // Page reload or subsequent auth changes: skip animation
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      clearTimeout(animationTimer);
    };
  }, [hasSeenSplash]);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {loading && (
        <div
          className={`fixed inset-0 z-50 flex h-screen w-screen flex-col items-center justify-center gap-6 transition-opacity duration-500 ${
            fadeOut ? "opacity-0" : "opacity-100"
          } ${!hasSeenSplash ? "bg-primary" : "bg-transparent"}`}
        >
          {!hasSeenSplash ? (
            <img className="max-h-96" key={gifKey} src={Splash} alt="Loading" />
          ) : (
            <>
              <div className="h-16 w-16 animate-spin rounded-full border-4 border-medium-gray border-t-primary"></div>
              <p className="text-center text-lg text-brand-gray">
                {t("common.loading")}...
              </p>
            </>
          )}
        </div>
      )}
      <div
        className={`transition-opacity duration-300 ${
          loading ? "opacity-0" : "opacity-100"
        }`}
      >
        {children}
      </div>
    </AuthContext.Provider>
  );
}
